import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import NavLink from './common/NavLink';
import CustomSelect from './CustomSelect';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, FormControl, Select, MenuItem } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import RequireVip from './RequireVip'
import { Helmet } from 'react-helmet-async'; // [cite: 394]
import { slugify } from '../utils/slugify'; 
import { auth } from './Firebase'; // Dodano import auth z Firebase
import { useCompetitions } from './CompetitionContext';
import SupportBox from './SupportBox';


const getTopGradientColor = (value) => {
  if (value === 0) return 'rgba(255, 255, 255, 0.3)';
  const scaledValue = value;
  const grayBase = 255;
  const r = Math.round(grayBase * (1 - scaledValue));
  const g = Math.round(grayBase * (1 - scaledValue) + 255 * scaledValue);
  const b = Math.round(grayBase * (1 - scaledValue) + 50 * scaledValue); // Lekkie niebieskie dla głębi
  const alpha = 0.3 + scaledValue * 0.7;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const formatChance = (chance, useTilde = false) => {
  if (typeof chance !== 'number' || isNaN(chance)) return '—';

  const percent = chance * 100;

  if (percent < 0.001) return useTilde ? '~0.0%' : '0.0%';
  if (percent > 99.999) return useTilde ? '~100%' : '100.0%';

  const decimals = ((percent < 0.1 && percent > 0) || (percent > 99.9 && percent < 100)) ? 2 : 1;
  const formatted = percent.toFixed(decimals);

  return useTilde && (percent < 0.01 || percent > 99.99)
    ? `~${formatted}%`
    : `${formatted}%`;
};

const getRelegationGradientColor = (value) => {
  if (value === 0) return 'rgba(255, 255, 255, 0.3)';
  const scaledValue = value;
  const grayBase = 255;
  const r = Math.round(grayBase * (1 - scaledValue) + 255 * scaledValue);
  const g = Math.round(grayBase * (1 - scaledValue));
  const b = Math.round(grayBase * (1 - scaledValue));
  const alpha = 0.3 + scaledValue * 0.7;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const convertUTCToLocal = (utcValue) => {
  let utcDate;
  if (typeof utcValue === 'number') {
    utcDate = new Date(utcValue);
  } else if (typeof utcValue === 'string') {
    utcDate = new Date(utcValue + 'Z');
  } else {
    console.error('Nieprawidłowy typ created_at:', typeof utcValue, utcValue);
    return 'Błąd daty';
  }
  return utcDate.toLocaleString('pl-PL', { timeZone: 'Europe/Warsaw' });
};

const Summary = () => {
  const { competitionId } = useParams();
  const { competitions, loading } = useCompetitions();
  const [data, setData] = useState({
    teamData: {},
    positionProbabilities: [],
    createdAt: null,
    penaltyMessages: [],
    competitionConfig: {},
  });
  const [leagueName, setLeagueName] = useState('');
  const [selectedSimulationId, setSelectedSimulationId] = useState(null);
  const [simulationList, setSimulationList] = useState([]);
  const [currentSimulationIndex, setCurrentSimulationIndex] = useState(0);
  const [error, setError] = useState(null);
  const [selectedComp, setSelectedComp] = useState(null); // ← Dodany stan dla selectedComp

  
  const simulationOptions = simulationList.map((sim, index) => ({
    value: index,
    label: sim.displayName,
  }));

  const fetchSimulationData = useCallback(async (simulationId, userId) => {
    try {
      const headers = userId ? { 'Authorization': `Bearer ${userId}` } : {};
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/simulation/${simulationId}`, { headers });
      setData(prev => ({ ...prev, ...response.data }));
    } catch (err) {
      setError('Błąd podczas pobierania danych symulacji.');
      console.error(err);
    }
  }, []);

	useEffect(() => {
	  if (loading || !competitionId) return;

	  const selectedComp = Object.values(competitions)
		.flat()
		.find(comp => comp.competition_id === parseInt(competitionId));

	  if (!selectedComp) {
		setError('Liga nie znaleziona.');
		return;
	  }
	  setSelectedComp(selectedComp); // ← Zapisujemy do stanu


	  const displayName =
		selectedComp.group && selectedComp.group !== 'NULL' && selectedComp.group.trim() !== ''
		  ? `${selectedComp.name} (${selectedComp.group.charAt(0).toUpperCase() + selectedComp.group.slice(1)})`
		  : selectedComp.name;
	  setLeagueName(displayName);
	  setData(prev => ({ ...prev, competitionConfig: selectedComp }));

	  // Pobieranie listy symulacji z nowego endpointu
	  console.log('Pobieram symulacje dla competitionId:', competitionId);
	  axios.get(`${process.env.REACT_APP_API_URL}/api/competition/${competitionId}/simulations`)
  .then(response => {
    console.log('Pobrano symulacje (raw):', response.data); // Debug raw data
    const simList = response.data.map(sim => ({
      ...sim,
	  userId: sim.user_id, // Jawne mapowanie user_id na userId
      displayName: sim.conditions !== "" ? `${convertUTCToLocal(sim.created_at)} (*)` : convertUTCToLocal(sim.created_at)
    }));
    // Poprawiony filtr:
    const filteredSimList = simList.filter(sim => {
      const isDefault = !sim.userId || sim.userId === ""; // null lub pusty string = default
      const isOwned = auth.currentUser?.uid && sim.userId === auth.currentUser.uid; // Tylko dla zalogowanego
      console.log(`Sim: ${sim.simulation_id}, userId: ${sim.userId}, isDefault: ${isDefault}, isOwned: ${isOwned}`); // Debug
      return isDefault || isOwned;
    });
    setSimulationList(filteredSimList);
    const initialIndex = 0;
    setCurrentSimulationIndex(initialIndex);
    const initialSimId = filteredSimList.length > 0 ? filteredSimList[initialIndex].simulation_id : null;
    setSelectedSimulationId(initialSimId);
    console.log('Filtered simList:', filteredSimList); // Debug po filtrze
		})
		.catch(err => {
		  setError('Błąd podczas pobierania listy symulacji.');
		  console.error('Błąd pobierania symulacji:', err.response?.data || err.message);
		});
	}, [competitionId, competitions, loading]);

  useEffect(() => {
    if (!selectedSimulationId) return;
    fetchSimulationData(selectedSimulationId, auth.currentUser?.uid);
  }, [selectedSimulationId, fetchSimulationData]);

  const handlePreviousSimulation = () => {
    if (currentSimulationIndex < simulationList.length - 1) {
      const newIndex = currentSimulationIndex + 1;
      setCurrentSimulationIndex(newIndex);
      setSelectedSimulationId(simulationList[newIndex].simulation_id);
    }
  };

  const handleNextSimulation = () => {
    if (currentSimulationIndex > 0) {
      const newIndex = currentSimulationIndex - 1;
      setCurrentSimulationIndex(newIndex);
      setSelectedSimulationId(simulationList[newIndex].simulation_id);
    }
  };

  const handleSimulationSelect = (event) => {
    const selectedIndex = event.target.value;
    setCurrentSimulationIndex(selectedIndex);
    setSelectedSimulationId(simulationList[selectedIndex].simulation_id);
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', padding: '20px' }}>
        <Typography variant="h6">Ładowanie...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', padding: '20px' }}>
        <Typography variant="h6">{error}</Typography>
      </Box>
    );
  }

  const teams = Object.keys(data.teamData).map(teamId => ({
    id: parseInt(teamId),
    name: data.teamData[teamId].name,
    expectedPoints: data.teamData[teamId].expectedPoints ?? 0,
    expectedPlace: data.teamData[teamId].expectedPlace ?? 0,
  })).sort((a, b) => a.expectedPlace - b.expectedPlace);

  const numTeams = teams.length;

  const summaryMatrix = teams.map(team => {
    const probs = data.positionProbabilities.filter(prob => prob.teamId === team.id);

    const promotionSpots = data.competitionConfig.promotion_spots || 0;
    const promotionPlayoffSpots = data.competitionConfig.promotion_playoff_spots || 0;
    const relegationSpots = data.competitionConfig.relegation_spots || 0;
    const relegationPlayoffSpots = data.competitionConfig.relegation_playoff_spots || 0;

    const promotionChance = promotionSpots > 0
      ? probs.filter(prob => prob.position >= 1 && prob.position <= promotionSpots)
          .reduce((sum, prob) => sum + prob.probability, 0)
      : 0;

    const promotionPlayoffChance = promotionPlayoffSpots > 0
      ? probs.filter(prob => prob.position >= 1 && prob.position <= (promotionSpots + promotionPlayoffSpots))
          .reduce((sum, prob) => sum + prob.probability, 0)
      : 0;

    // Pobieramy gotowe prawdopodobieństwo z API i dzielimy przez 100, 
	// ponieważ API zwraca wartości 0-100 (np. 49.38), 
	// a Twoje funkcje gradientów oczekują wartości 0-1.
	const relegationChance = data.teamRelegationProbabilities && data.teamRelegationProbabilities[team.id]
	  ? data.teamRelegationProbabilities[team.id] / 100
	  : 0;

    // 2. Obliczamy całkowite ryzyko (Spadek + Baraż)
	let totalRisk = 0;
	const pSpots = data.competitionConfig.relegation_playoff_spots || 0;

	probs.forEach(p => {
	  const pos = p.position;
	  // Sprawdzamy szansę spadku dla slotu przesuniętego o liczbę miejsc barażowych
	  const targetPos = pos + pSpots;
	  
	  // WAŻNE: używamy .toString(), bo klucze w obiektach JSON to stringi
	  const probAtTarget = (
		data.relegationSlotProbabilities[targetPos.toString()] || 
		(targetPos > numTeams ? 100 : 0)
	  ) / 100;

	  totalRisk += (p.probability) * probAtTarget;
	});

	// 3. Wynik to suma. Upewniamy się, że nie jest mniejsza niż sam spadek (przez zaokrąglenia)
	const relegationPlayoffChance = totalRisk;

    return {
      teamId: team.id,
      teamName: team.name,
      promotionChance,
      promotionPlayoffChance,
      relegationChance,
      relegationPlayoffChance,
      expectedPlace: team.expectedPlace ?? 0,
      expectedPoints: team.expectedPoints ?? 0,
    };
  });

  const columnCount = 2 + // Drużyna + Śr. Miejsce + Śr. Punkty
    (data.competitionConfig.promotion_spots > 0 ? 1 : 0) +
    (data.competitionConfig.promotion_playoff_spots > 0 ? 1 : 0) +
    (data.competitionConfig.relegation_playoff_spots > 0 ? 1 : 0) +
    (data.competitionConfig.relegation_spots > 0 ? 1 : 0);
  const minTableWidth = columnCount * 100;
  
  const pageTitle = `${leagueName} - Podsumowanie Symulacji | SymuLiga`;
  const pageDesc = `Zobacz prognozę końcową dla ${leagueName}. Procentowe szanse na awans, spadek i baraże. Średnia oczekiwana liczba punktów na koniec sezonu.`;

  return (
  <>
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDesc} />
      <meta property="og:title" content={`${leagueName} - Prognoza końcowa tabeli`} />
      <meta property="og:description" content="Sprawdź matematyczne podsumowanie sezonu. Ranking ELO, predykcje, punkty oczekiwane, oczekiwana pozycja i symulacja 10000 scenariuszy." />
    </Helmet>
    <Box sx={{ padding: { xs: '0px', sm: '5px', md: '10px' } }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: { xs: 'center', sm: 'space-between' },
          alignItems: 'center',
          mb: 3,
          gap: 1,
        }}
      >
        <Typography variant="h5" component="h1" sx={{ textAlign: { xs: 'center', sm: 'center' }, fontSize: { xs: '1.0rem', sm: '1.2rem' } }}>
          Podsumowanie Symulacji - {leagueName}
        </Typography>
		<Typography variant="subtitle1" component="h2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
			Podsumowanie szans drużyn na podstawie aktualnego rankingu ELO i terminarza
		</Typography>
        {simulationList.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            <IconButton
              onClick={handlePreviousSimulation}
              disabled={currentSimulationIndex >= simulationList.length - 1}
              size="small"
            >
              <ArrowBackIosIcon fontSize="small" />
            </IconButton>
            <CustomSelect
			  value={currentSimulationIndex}
			  onChange={handleSimulationSelect}
			  options={simulationOptions}
			  displayEmpty // Przekazanie propów specyficznych dla Select
			  inputProps={{ 'aria-label': 'Wybierz symulację' }} // Przekazanie inputProps
			/>
            <IconButton
              onClick={handleNextSimulation}
              disabled={currentSimulationIndex === 0}
              size="small"
            >
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>

      {numTeams === 0 ? (
        <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          Ładowanie...
        </Typography>
      ) : (
        <>
          {simulationList[currentSimulationIndex]?.conditions !== "" && (
            <Typography variant="subtitle1" gutterBottom sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, textAlign: 'center' }}>
              Symulacja warunkowa!<br />
              <span dangerouslySetInnerHTML={{ __html: simulationList[currentSimulationIndex]?.conditions?.replace(/\n/g, '<br />') || '' }} />
            </Typography>
          )}
          <RequireVip variant='vip-teaser' requirement={selectedComp.stats_requirement}>
			  <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
				<TableContainer
				  component={Paper}
				  sx={{
					marginBottom: 1,
					width: 'auto',
					display: 'inline-block',
					overflowX: 'auto',
					borderRadius: '12px',
					'-webkit-overflow-scrolling': 'touch',
					position: 'relative',
					'&::-webkit-scrollbar': {
					  width: '8px',
					  height: '8px',
					},
					'&::-webkit-scrollbar-thumb': {
					  backgroundColor: 'rgba(0,0,0,0.3)',
					  borderRadius: '4px',
					},
					'&::before': {
					  content: '"SymuLiga.pl"',
					  position: 'absolute',
					  top: '50%',
					  left: '50%',
					  transform: 'translate(-50%, -50%) rotate(-45deg)',
					  fontSize: { xs: '3rem', sm: '4rem', md: '5rem' },
					  fontWeight: 'bold',
					  color: 'rgba(0, 0, 0, 0.15)',
					  pointerEvents: 'none',
					  zIndex: 0,
					  whiteSpace: 'nowrap',
					},
				  }}
				>
				  

				  <Table
					sx={{
					  minWidth: minTableWidth,
					  tableLayout: 'auto',
					  borderCollapse: 'collapse',
					  position: 'relative',
					  zIndex: 1,
					}}
				  >
					<TableHead>
					  <TableRow>
					  <TableCell
							sx={{
							  position: 'sticky',
							  left: 0,
							  top: 0,
							  zIndex: 4, // Wyższy zindex niż Drużyna
							  backgroundColor: 'background.paper',
							  width: '10px',
							  minWidth: '10px',
							  padding: '2px',
							  textAlign: 'center',
							  fontSize: { xs: '0.8rem', sm: '0.9rem' },
							  fontWeight: 'bold',
							borderRight: '1px solid rgba(80, 80, 80, 1)',
							}}
						  >
							#
						  </TableCell>
						<TableCell
						  sx={{
							position: 'sticky',
							left: 0,
							top: 0,
							zIndex: 5,
							backgroundColor: 'background.paper',
							width: 'auto', // Dopasowanie do zawartości
							minWidth: 40, // Minimalna szerokość dostosowana
							padding: '5px',
							fontSize: { xs: '0.8rem', sm: '0.9rem' },
							borderRight: '1px solid rgba(224, 224, 224, 1)',
						  }}
						>
						  Drużyna
						</TableCell>
						{data.competitionConfig.promotion_spots > 0 && (
						  <TableCell
							align="center"
							sx={{
							  position: 'sticky',
							  top: 0,
							  zIndex: 2,
							  backgroundColor: 'background.paper',
							  width: 'auto', // Proporcjonalne dostosowanie
							  minWidth: 70, // Minimalna szerokość dostosowana
							  padding: '5px',
							  fontSize: { xs: '0.7rem', sm: '0.8rem' },
							  borderBottom: '2px solid rgba(224, 224, 224, 1)',
							}}
						  >
							{data.competitionConfig.promotion_spots_name || 'Awans'}
						  </TableCell>
						)}
						{data.competitionConfig.promotion_playoff_spots > 0 && (
						  <TableCell
							align="center"
							sx={{
							  position: 'sticky',
							  top: 0,
							  zIndex: 2,
							  backgroundColor: 'background.paper',
							  width: 'auto', // Proporcjonalne dostosowanie
							  minWidth: 70, // Minimalna szerokość dostosowana
							  padding: '5px',
							  fontSize: { xs: '0.8rem', sm: '0.9rem' },
							  borderBottom: '2px solid rgba(224, 224, 224, 1)',
							}}
						  >
							{data.competitionConfig.promotion_playoff_spots_name || 'Baraże o awans'}
						  </TableCell>
						)}
						{data.competitionConfig.relegation_playoff_spots > 0 && (
						  <TableCell
							align="center"
							sx={{
							  position: 'sticky',
							  top: 0,
							  zIndex: 2,
							  backgroundColor: 'background.paper',
							  width: 'auto', // Proporcjonalne dostosowanie
							  minWidth: 70, // Minimalna szerokość dostosowana
							  padding: '5px',
							  fontSize: { xs: '0.8rem', sm: '0.9rem' },
							  borderBottom: '2px solid rgba(224, 224, 224, 1)',
							}}
						  >
							{data.competitionConfig.relegation_playoff_spots_name || 'Baraże o utrzymanie'}
						  </TableCell>
						)}
						{data.competitionConfig.relegation_spots > 0 && (
						  <TableCell
							align="center"
							sx={{
							  position: 'sticky',
							  top: 0,
							  zIndex: 2,
							  backgroundColor: 'background.paper',
							  width: 'auto', // Proporcjonalne dostosowanie
							  minWidth: 70, // Minimalna szerokość dostosowana
							  padding: '5px',
							  fontSize: { xs: '0.8rem', sm: '0.9rem' },
							  borderBottom: '2px solid rgba(224, 224, 224, 1)',
							}}
						  >
							{data.competitionConfig.relegation_spots_name || 'Spadek'}
						  </TableCell>
						)}
						<TableCell
						  align="center"
						  sx={{
							position: 'sticky',
							top: 0,
							zIndex: 2,
							backgroundColor: 'background.paper',
							width: 'auto', // Proporcjonalne dostosowanie
							minWidth: 50, // Minimalna szerokość dostosowana
							padding: '5px',
							fontSize: { xs: '0.8rem', sm: '0.9rem' },
							borderBottom: '2px solid rgba(224, 224, 224, 1)',
						  }}
						>
						  xPos
						</TableCell>
						<TableCell
						  align="center"
						  sx={{
							position: 'sticky',
							top: 0,
							zIndex: 2,
							backgroundColor: 'background.paper',
							width: 'auto', // Proporcjonalne dostosowanie
							minWidth: 50, // Minimalna szerokość dostosowana
							padding: '5px',
							fontSize: { xs: '0.8rem', sm: '0.9rem' },
							borderBottom: '2px solid rgba(224, 224, 224, 1)',
						  }}
						>
						  xPts
						</TableCell>
					  </TableRow>
					</TableHead>
					<TableBody>
					  {summaryMatrix.map((row, index) => (
						<TableRow key={row.teamId}>
						 <TableCell
						  sx={{
							position: 'sticky',
							left: 0,
							zIndex: 2,
							backgroundColor: 'background.paper',
							textAlign: 'center',
							width: '10px',
							minWidth: '10px',
							padding: '2px',
							fontSize: { xs: '0.7rem', sm: '0.8rem' },
							color: 'rgba(255,255,255,0.6)',
							borderRight: '1px solid rgba(80, 80, 80, 1)',
						  }}
						>
						  {index + 1}
						</TableCell>
						  <TableCell
						  component={NavLink}
						  to={`/team/${row.teamId}/${slugify(row.teamName)}`}
							sx={{
							  position: 'sticky',
							  left: 0,
							  zIndex: 3,
							  backgroundColor: 'background.paper',
							  width: 'auto', // Dopasowanie do zawartości
							  minWidth: 40, // Minimalna szerokość dostosowana
							  padding: '5px',
							  fontSize: { xs: '0.7rem', sm: '0.8rem' },
							  borderRight: '1px solid rgba(224, 224, 224, 1)',
							}}
						  >
							{row.teamName}
						  </TableCell>
						  {data.competitionConfig.promotion_spots > 0 && (
							<TableCell
							  align="center"
							  sx={{
								width: 'auto', // Proporcjonalne dostosowanie
								minWidth: 70, // Minimalna szerokość dostosowana
								backgroundColor: getTopGradientColor(row.promotionChance),
								color: 'black', // Zawsze czarny dla awansu
								fontWeight: 'bold',
								fontSize: { xs: '0.8rem', sm: '0.9rem' },
								padding: '5px',
								textShadow: '0.5px 0.5px 1px rgba(0, 0, 0, 0.3)',
								transition: 'background-color 0.3s',
								'@media (hover: hover)': {
								  '&:hover': {
									backgroundColor: getTopGradientColor(Math.min(row.promotionChance + 0.1, 1)),
									transform: 'scale(1.05)',
								  },
								},
							  }}
							>
								{formatChance(row.promotionChance, false)}
							</TableCell>
						  )}
						  {data.competitionConfig.promotion_playoff_spots > 0 && (
							<TableCell
							  align="center"
							  sx={{
								width: 'auto', // Proporcjonalne dostosowanie
								minWidth: 70, // Minimalna szerokość dostosowana
								backgroundColor: getTopGradientColor(row.promotionPlayoffChance),
								color: 'black', // Zawsze czarny
								fontWeight: 'bold',
								fontSize: { xs: '0.8rem', sm: '0.9rem' },
								padding: '5px',
								textShadow: '0.5px 0.5px 1px rgba(0, 0, 0, 0.3)',
								transition: 'background-color 0.3s',
								'@media (hover: hover)': {
								  '&:hover': {
									backgroundColor: getTopGradientColor(Math.min(row.promotionPlayoffChance + 0.1, 1)),
									transform: 'scale(1.05)',
								  },
								},
							  }}
							>
								{formatChance(row.promotionPlayoffChance, false)}
							</TableCell>
						  )}
						  {data.competitionConfig.relegation_playoff_spots > 0 && (
							<TableCell
							  align="center"
							  sx={{
								width: 'auto', // Proporcjonalne dostosowanie
								minWidth: 70, // Minimalna szerokość dostosowana
								backgroundColor: getRelegationGradientColor(row.relegationPlayoffChance),
								color: row.relegationPlayoffChance > 0.5 ? 'white' : 'black',
								fontWeight: 'bold',
								fontSize: { xs: '0.8rem', sm: '0.9rem' },
								padding: '5px',
								textShadow: '0.5px 0.5px 1px rgba(0, 0, 0, 0.3)',
								transition: 'background-color 0.3s',
								'@media (hover: hover)': {
								  '&:hover': {
									backgroundColor: getRelegationGradientColor(Math.min(row.relegationPlayoffChance + 0.1, 1)),
									transform: 'scale(1.05)',
								  },
								},
							  }}
							>
							  {formatChance(row.relegationPlayoffChance, false)}
							</TableCell>
						  )}
						  {data.competitionConfig.relegation_spots > 0 && (
							<TableCell
							  align="center"
							  sx={{
								width: 'auto', // Proporcjonalne dostosowanie
								minWidth: 70, // Minimalna szerokość dostosowana
								backgroundColor: getRelegationGradientColor(row.relegationChance),
								color: row.relegationChance > 0.5 ? 'white' : 'black',
								fontWeight: 'bold',
								fontSize: { xs: '0.8rem', sm: '0.9rem' },
								padding: '5px',
								textShadow: '0.5px 0.5px 1px rgba(0, 0, 0, 0.3)',
								transition: 'background-color 0.3s',
								'@media (hover: hover)': {
								  '&:hover': {
									backgroundColor: getRelegationGradientColor(Math.min(row.relegationChance + 0.1, 1)),
									transform: 'scale(1.05)',
								  },
								},
							  }}
							>
							  {formatChance(row.relegationChance, false)}
							</TableCell>
						  )}
						  <TableCell
							align="center"
							sx={{
							  width: 'auto', // Proporcjonalne dostosowanie
							  minWidth: 50, // Minimalna szerokość dostosowana
							  backgroundColor: 'rgba(128, 128, 128, 0.4)',
							  color: 'white',
							  fontWeight: 'bold',
							  fontSize: { xs: '0.8rem', sm: '0.9rem' },
							  padding: '5px',
							  textShadow: '0.5px 0.5px 1px rgba(0, 0, 0, 0.3)',
							  transition: 'background-color 0.3s',
							  '@media (hover: hover)': {
								'&:hover': {
								  backgroundColor: 'rgba(255, 255, 255, 0.2)',
								  transform: 'scale(1.05)',
								},
							  },
							}}
						  >
							{(row.expectedPlace || 0).toFixed(1)}
						  </TableCell>
						  <TableCell
							align="center"
							sx={{
							  width: 'auto', // Proporcjonalne dostosowanie
							  minWidth: 50, // Minimalna szerokość dostosowana
							  backgroundColor: 'rgba(128, 128, 128, 0.4)',
							  color: 'white',
							  fontWeight: 'bold',
							  fontSize: { xs: '0.8rem', sm: '0.9rem' },
							  padding: '5px',
							  textShadow: '0.5px 0.5px 1px rgba(0, 0, 0, 0.3)',
							  transition: 'background-color 0.3s',
							  '@media (hover: hover)': {
								'&:hover': {
								  backgroundColor: 'rgba(255, 255, 255, 0.2)',
								  transform: 'scale(1.05)',
								},
							  },
							}}
						  >
							{(row.expectedPoints || 0).toFixed(1)}
						  </TableCell>
						</TableRow>
					  ))}
					</TableBody>
				  </Table>
				</TableContainer>
			  </Box>
			</RequireVip>

          {/* Logika sprawdzająca, czy wartości prawdopodobieństwa są zróżnicowane */}
			{(() => {
			  const probs = Object.values(data.relegationSlotProbabilities || {});
			  const hasDifferentValues = probs.length > 0 && !probs.every(val => val === probs[0]);

			  if (!hasDifferentValues) return null;

			  return (
			  <Box
				sx={{
				  display: 'flex',
				  flexDirection: 'column',
				  justifyContent: { xs: 'center', sm: 'space-between' },
				  alignItems: 'center',
				  mb: 1,
				  gap: 1,
				}}
			  >
				
				<Typography variant="subtitle1" component="h2" sx={{ color: 'text.secondary' }}>
					Ryzyko spadku dla drużyny zajmującej konkretne miejsce w tej lidze:
				</Typography>
				
				
					
				  <TableContainer
					component={Paper}
					sx={{
					  width: 'auto',
					  minWidth: 320,
					  borderRadius: '12px',
					  overflow: 'hidden',
					  backgroundColor: 'background.paper',
					  boxShadow: '0px 4px 20px rgba(0,0,0,0.4)', // Lekki cień dla odróżnienia
					}}
				  >
					<Table size="small" sx={{ tableLayout: 'auto' }}>
					  <TableHead>
						<TableRow>
						  <TableCell
							sx={{
							  backgroundColor: 'background.paper',
							  fontWeight: 'bold',
							  padding: '12px 20px',
							  fontSize: { xs: '0.8rem', sm: '0.9rem' },
							  borderBottom: '2px solid rgba(224, 224, 224, 1)',
							}}
						  >
							Pozycja w tabeli
						  </TableCell>
						  <TableCell
							align="center"
							sx={{
							  backgroundColor: 'background.paper',
							  fontWeight: 'bold',
							  padding: '12px 20px',
							  fontSize: { xs: '0.8rem', sm: '0.9rem' },
							  borderBottom: '2px solid rgba(224, 224, 224, 1)',
							}}
						  >
							Ryzyko spadku
						  </TableCell>
						</TableRow>
					  </TableHead>
					  <TableBody>
						{Object.entries(data.relegationSlotProbabilities)
						  .sort(([posA], [posB]) => parseInt(posA) - parseInt(posB))
						  .map(([position, probability]) => (
							<TableRow key={position}>
							  <TableCell
								sx={{
								  padding: '10px 20px',
								  fontSize: { xs: '0.75rem', sm: '0.85rem' },
								  borderRight: '1px solid rgba(224, 224, 224, 1)',
								}}
							  >
								Miejsce {position}
							  </TableCell>
							  <TableCell
								align="center"
								sx={{
								  padding: '10px 20px',
								  fontSize: { xs: '0.8rem', sm: '0.9rem' },
								  fontWeight: 'bold',
								  backgroundColor: getRelegationGradientColor(probability / 100),
								  color: (probability / 100) > 0.5 ? 'white' : 'black',
								  textShadow: '0.5px 0.5px 1px rgba(0, 0, 0, 0.3)',
								}}
							  >
								{probability.toFixed(1)}%
							  </TableCell>
							</TableRow>
						  ))}
					  </TableBody>
					</Table>
				  </TableContainer>
				  <Typography component={NavLink}	to={`https://x.com/symuligaPL/status/2054518867263684987`}sx={{ padding: '2px', color: 'white' }}>
					Sprawdź jak to obliczamy!
				</Typography>
				</Box>
			  );
			})()}

          {data.penaltyMessages && data.penaltyMessages.length > 0 && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: 'rgba(255, 255, 255, 0.05)', mx: 'auto', width: 'fit-content', borderRadius: '8px', alignItems: 'center', textAlign: 'center' }}>
              <Typography variant="h6" sx={{ mb: 1, color: '#ffffff', fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                Informacje o karach punktowych
              </Typography>
              {data.penaltyMessages.map((message, index) => (
                <Typography
                  key={index}
                  variant="body2"
                  sx={{
                    color: '#ff6f61',
                    fontStyle: 'italic',
					alignItems: 'center',
					textAlign: 'center',
                    mb: 0.5,
					width: 'fit-content', // Ramka dopasowuje się do zawartości
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  }}
                >
                  {message}
                </Typography>
              ))}
            </Box>
          )}
        </>
      )}
    </Box>
	<SupportBox />
	</>
  );
};

export default Summary;